// FOLLOW-UP SCHEDULING AND TEMPLATES (Run O §1). Pure: no I/O, no clock reads.
//
// ⚠️ THE SWEEP MAKES NO AI CALL, and that is why this file exists. Whether a
// follow-up is appropriate is decided at the moment the bot writes each reply,
// inside the call that already happens (`followUp: needed | not_needed`). The
// scheduler only OBEYS it, by sending the club's own template text. That keeps the
// "no AI call fires from a poll" rule intact without an amendment.
//
// ⚠️ THE DETERMINISTIC OVERRIDES BEAT THE MODEL, ALWAYS. A model that says "needed"
// on a conversation that is stopped, handed over, booked or classified does not get
// a follow-up. Following up on "not interested" is how a bot becomes spam.

import { instantIsoToWallClock, wallClockToInstantMs } from '../scheduleTime.js';
import { localDateString } from './promotions.js';
import { addLocalDays } from './staffedHours.js';
import { safeFirstName } from './fixedMessages.js';
import type {
  BotConversation,
  BotFollowUps,
  FollowUpSequence,
  FollowUpStep,
} from '../../types/leadBot.js';

export const DEFAULT_FOLLOWUP_WINDOW = { start: '08:00', end: '20:00' } as const;
export const FOLLOW_UP_TOKENS = ['FirstName', 'AssistantName', 'ClubName', 'Offer', 'OfferEnds'] as const;
export type FollowUpToken = (typeof FOLLOW_UP_TOKENS)[number];

export type FollowUpJudgement = 'needed' | 'not_needed';

// ---- templates --------------------------------------------------------------

export interface FollowUpTokenValues {
  FirstName: string;
  AssistantName: string;
  ClubName: string;
  /** '' when there is no offer to name (the step then does not send). */
  Offer: string;
  /** '' unless a LIVE campaign supplies it. */
  OfferEnds: string;
}

export type RenderResult = { ok: true; text: string } | { ok: false; missing: string };

/**
 * Render a club-written template. ⚠️ A step with any token that cannot be resolved
 * does NOT send: no "running until ." with a hole in it. An unknown token
 * ({{Whatever}}) is also unresolvable, so a typo can never publish literally.
 */
export function renderFollowUp(template: string, values: FollowUpTokenValues): RenderResult {
  let missing: string | null = null;
  const text = template.replace(/\{\{\s*([A-Za-z]+)\s*\}\}/g, (_all, name: string) => {
    const value = (values as unknown as Record<string, string | undefined>)[name];
    if (value === undefined || value.trim() === '') {
      missing ??= name;
      return '';
    }
    return value.trim();
  });
  if (missing !== null) return { ok: false, missing };
  if (/\{\{|\}\}/.test(text)) return { ok: false, missing: 'unclosed token' };
  return { ok: true, text: text.replace(/\s+/g, ' ').trim() };
}

/** Names the lead's first name only if it is name-shaped (untrusted input). */
export function leadFirstNameForTemplate(rawName: string | null | undefined): string {
  return safeFirstName(rawName);
}

// ---- the window -------------------------------------------------------------

function minutesOf(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/**
 * Move an instant INTO the follow-up window, in the club's local time. Inside the
 * window it is unchanged; before it opens it moves to today's opening; after it
 * closes it moves to tomorrow's opening. ⚠️ Follow-ups only: the opening message
 * and replies to a lead go out at any hour.
 */
export function applyFollowUpWindow(
  dueMs: number,
  timezone: string,
  window: { start: string; end: string },
): number {
  const start = minutesOf(window.start) ?? minutesOf(DEFAULT_FOLLOWUP_WINDOW.start)!;
  const end = minutesOf(window.end) ?? minutesOf(DEFAULT_FOLLOWUP_WINDOW.end)!;
  if (end <= start) return dueMs; // a nonsense window must not silence the bot forever
  const wall = instantIsoToWallClock(new Date(dueMs).toISOString(), timezone);
  if (!wall) return dueMs;
  const [, time] = wall.split('T') as [string, string];
  const local = minutesOf(time)!;
  if (local >= start && local < end) return dueMs;
  const ymd = local < start ? localDateString(dueMs, timezone) : addLocalDays(dueMs, timezone, 1);
  const hh = String(Math.floor(start / 60)).padStart(2, '0');
  const mm = String(start % 60).padStart(2, '0');
  return wallClockToInstantMs(`${ymd}T${hh}:${mm}`, timezone) ?? dueMs;
}

// ---- the overrides ----------------------------------------------------------

type OverrideInput = Pick<
  BotConversation,
  'state' | 'outcome' | 'distressAt' | 'stopReason' | 'appointmentId' | 'handoffAt' | 'speedToLeadStandbyAt' | 'staffTakeoverAt'
>;

/**
 * Why a conversation may NOT be followed up, whatever the model said. null = it may.
 * Every one of these is deterministic and none consults the model.
 */
export function followUpBlockedReason(conv: Partial<OverrideInput>): string | null {
  if (conv.state && conv.state !== 'active') return `conversation is ${conv.state}`;
  if (conv.distressAt) return 'distress';
  if (conv.stopReason) return `stopped (${conv.stopReason})`;
  if (conv.handoffAt) return 'handed over to a person';
  // A PERSON has the texts for this lead (staff takeover, Leads centre v2): nothing is followed up until it is handed back.
  if (conv.staffTakeoverAt) return 'a person has the texts';
  if (conv.appointmentId) return 'booked';
  // Speed to Lead standby: Sam is held back (waiting on a call, or the call reached the lead). Inbound texts
  // are still answered; nothing is FOLLOWED UP while it is set.
  if (conv.speedToLeadStandbyAt) return 'speed to lead standby';
  if (conv.outcome && conv.outcome !== 'unresolved') return `outcome is ${conv.outcome}`;
  return null;
}

// ---- scheduling -------------------------------------------------------------

export interface ScheduleInput {
  conversation: Partial<OverrideInput> & {
    /** The lead has sent at least one message. */
    leadHasReplied: boolean;
  };
  followUps: BotFollowUps | undefined;
  /** The model's judgement on the reply just sent; undefined for the opener or a fixed message. */
  judgement: FollowUpJudgement | undefined;
  /** The follow-up that was JUST sent, if this outbound was one; the sequence then continues. */
  justSent: { sequence: FollowUpSequence; step: number } | null;
  /** The last message in the conversation, either side: delays are measured from here. */
  lastMessageAtMs: number;
  timezone: string;
  /** Demo only: compress every delay by this factor (the conference demo shows a follow-up in minutes). */
  delayScale?: number;
}

export interface ScheduledFollowUp {
  atMs: number;
  sequence: FollowUpSequence;
  step: number;
}

/**
 * Decide the NEXT follow-up after an outbound message, or null for none.
 *
 * - The lead has never replied: the no-response sequence, from its first step (or the
 *   step after the one just sent).
 * - The lead has replied: the incomplete sequence, ONLY if the model judged the
 *   conversation open (`needed`). A missing judgement (a fixed reply, a handover) is
 *   treated as not_needed: silence is the safe default.
 * - A follow-up that was just sent continues its own sequence regardless of judgement.
 */
export function nextFollowUp(input: ScheduleInput): ScheduledFollowUp | null {
  if (!input.followUps) return null;
  if (followUpBlockedReason(input.conversation) !== null) return null;

  let sequence: FollowUpSequence;
  let fromStep: number;
  if (input.justSent) {
    sequence = input.justSent.sequence;
    fromStep = input.justSent.step + 1;
  } else if (!input.conversation.leadHasReplied) {
    sequence = 'no_response';
    fromStep = 0;
  } else {
    if (input.judgement !== 'needed') return null;
    sequence = 'incomplete';
    fromStep = 0;
  }

  const steps: FollowUpStep[] = sequence === 'no_response' ? input.followUps.noResponse : input.followUps.incomplete;
  for (let i = fromStep; i < steps.length; i += 1) {
    const step = steps[i]!;
    if (!step.enabled) continue;
    const scale = input.delayScale && input.delayScale > 1 ? input.delayScale : 1;
    const delay = Math.max(1, Math.floor(step.delayMinutes / scale));
    const raw = input.lastMessageAtMs + delay * 60_000;
    return { atMs: applyFollowUpWindow(raw, input.timezone, input.followUps.window), sequence, step: i };
  }
  return null;
}

/** "No response 2 of 7, due Thu 2:15pm" for the conversation detail. */
export function describeScheduledFollowUp(
  sequence: FollowUpSequence,
  step: number,
  total: number,
  dueMs: number,
  timezone: string,
): string {
  const label = sequence === 'no_response' ? 'No response' : 'Conversation incomplete';
  let when: string;
  try {
    when = new Intl.DateTimeFormat('en-AU', {
      timeZone: timezone,
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(dueMs));
  } catch {
    when = new Date(dueMs).toISOString();
  }
  return `${label} ${step + 1} of ${total}, due ${when}`;
}
